import axios from "../services/axios";
import parseError from "../../utils/errorParse";
import { ensureFreshToken, getStoredAccessToken, isTokenExpired } from "../../helpers/tokenManager";

function withAPIKeys(params) {
  var tenantId = params?.tenantId;
  var forcedToken = params?.forcedToken;
  var extraHeaders = params?.headers;
  if(forcedToken){
    return {
      headers: {
        Authorization: `Bearer ${forcedToken}`,
        "Content-Type": "application/json",
        ...extraHeaders,
      },
    };
  }

  // Keeps the token fresh in the background; the token manager's proactive
  // scheduler is the primary mechanism, this is just a safety net.
  ensureFreshToken().catch(() => {});

  const token = getStoredAccessToken();
  return {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      "Content-Type": "application/json",
      ...(tenantId && { "Tenant-ID": tenantId }),
      ...extraHeaders,
    },
  };
}

function withMultipartAPIKeys(timeoutMs) {
  ensureFreshToken().catch(() => {});

  const token = getStoredAccessToken();
  return {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      "Content-Type": "multipart/form-data",
    },
    ...(timeoutMs && { timeout: timeoutMs }),
  };
}

function CustomHeadersForPdf() {
  const token = getStoredAccessToken();
  return {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      Accept: "application/pdf",
    },
    responseType: "blob",
  };
}

// The WSR generate endpoint returns either a PDF (still being drafted) or JSON (period
// already locked - server sends back the confirmed report's URL instead of rendering).
// The backend has ReturnHttpNotAcceptable enabled, so Accept must list both or the JSON
// branch gets rejected with 406 before the frontend ever sees it.
function CustomHeadersForReport() {
  const token = getStoredAccessToken();
  return {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      Accept: "application/pdf, application/json",
    },
    responseType: "blob",
  };
}

const addPageNumber = (url, params) => {
  let str = "";
  if (params.pageNumber) {
    str = `pageNumber=${params.pageNumber}`;
  }
  if (params.pageSize) {
    str = `${str}${str ? "&" : ""}pageSize=${params.pageSize}`;
  }

  return str ? `${url}?${str}` : url;
};

export const apiCall = {
  pagination: (payload) =>
    axios
      .get(payload.url, withAPIKeys())
      .then((response) => response)
      .catch((error) => parseError(error)),
  get: (payload) =>
    axios
      .get(payload.url, withAPIKeys(payload.tenantId))
      .then((response) => {
        return response.data;
      })
      .catch((error) => parseError(error)),
  post: (payload) =>
    axios
      .post(payload.url, payload.params, withAPIKeys({tenantId: payload.tenantId, forcedToken: payload.forcedToken, headers: payload.headers}))
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        const err = parseError(error);
        throw err;
        // throw new Error(JSON.stringify(err.data));
        // throw new Error(JSON.stringify(err.data.errors));
      }),
  put: (payload) =>
    axios
      .put(payload.url, payload.params, withAPIKeys())
      .then((response) => response.data)
      .catch((error) => {
        const err = parseError(error);
        throw err;
      }),
  patch: (payload) =>
    axios
      .patch(payload.url, payload.params, withAPIKeys())
      .then((response) => response.data)
      .catch((error) => {
        const err = parseError(error);
        throw err;
      }),
  delete: (payload) =>
    axios
      .delete(payload.url, withAPIKeys())
      .then((response) => response.data)
      .catch((error) => {
        const err = parseError(error);
        // throw new Error(err.data.message);
        throw err;
      }),
  file: (payload) =>
    axios
      .put(payload.url, payload.params, withMultipartAPIKeys())
      .then((response) => response.data)
      .catch((error) => parseError(error)),
  filePost: (payload) =>
    axios
      .post(payload.url, payload.params, withMultipartAPIKeys(payload.timeoutMs))
      .then((response) => response.data)
      .catch((error) => parseError(error)),
  pdf: (payload) =>
    axios
      .get(payload.url, CustomHeadersForPdf())
      .then((response) => response.data)
      .catch((error) => parseError(error)),
  pdfPost: (payload) =>
    axios
      .post(payload.url,payload.params, CustomHeadersForPdf())
      .then((response) => response.data)
      .catch((error) => parseError(error)),
  // Used only by the WSR generate endpoints: the response is either a fresh PDF blob
  // (period not yet locked - draft, needs Confirm) or JSON describing the period's
  // already-confirmed/locked report (no regeneration, no Confirm step needed).
  reportPost: (payload) =>
    axios
      .post(payload.url, payload.params, CustomHeadersForReport())
      .then(async (response) => {
        const contentType = response.headers?.["content-type"] || "";
        if (contentType.includes("application/json")) {
          const text = await response.data.text();
          return { kind: "locked", ...JSON.parse(text) };
        }
        return {
          kind: "generated",
          blob: response.data,
          cacheKey: response.headers?.["x-report-cache-key"],
          period: response.headers?.["x-report-period"],
        };
      })
      .catch((error) => parseError(error)),
  getFile: (payload) => {
    const token = getStoredAccessToken();
    return axios
      .get(payload.url, {headers: {...payload.headers, ...(token && { Authorization: `Bearer ${token}` })}, responseType: payload.responseType})
      .then((response) => response)
      .catch((error) => parseError(error));
  },
};

export const isTokenValid = () => !isTokenExpired();

export const buildQueryParams = (queryObject) => {
  const params = new URLSearchParams();
  for (const key in queryObject) {
    const value = queryObject[key];
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  }

  return params.toString();
};
export { handleLogout } from "../../helpers/tokenManager";
